import {
  useState, useCallback, useMemo, memo,
} from 'react';
import { motion } from 'framer-motion';
import { useRSVP } from '@/hooks/useRSVP';
import { useToastContext } from '@/hooks/useToastContext';
import { MIN_GUEST_COUNT, MAX_GUEST_COUNT } from '@/constants/validation';
import { validateRSVPForm } from '@/utils/validation';
import type { RSVPFormData } from '@/types/rsvp';
import styles from './RSVPForm.module.css';

type RSVPFormProps = {
  inviteId: string;
  onSuccess: () => void;
};

/**
 * Форма RSVP (подтверждение присутствия)
 *
 * Позволяет гостям ответить на приглашение:
 * - Подтвердить/отклонить присутствие
 * - Указать количество гостей
 * - Оставить сообщение
 */
const RSVPForm = memo(({ inviteId, onSuccess }: RSVPFormProps) => {
  const {
    submitRSVP, loading, error, success,
  } = useRSVP();
  const { showError } = useToastContext();

  const [response, setResponse] = useState<'yes' | 'no' | null>(null);
  const [guestCount, setGuestCount] = useState(MIN_GUEST_COUNT);
  const [guestNames, setGuestNames] = useState<string[]>(['']);
  const [message, setMessage] = useState('');

  const handleResponseChange = useCallback((value: 'yes' | 'no') => {
    setResponse(value);
    // Если ответ "нет", не нужно количество гостей
    if (value === 'no') {
      setGuestCount(MIN_GUEST_COUNT);
      setGuestNames(['']);
    } else if (value === 'yes') {
      // Если ответ "да", устанавливаем количество гостей
      setGuestCount(MIN_GUEST_COUNT);
    }
  }, []);

  const handleGuestCountChange = useCallback((count: number) => {
    if (count < MIN_GUEST_COUNT || count > MAX_GUEST_COUNT) {
      return;
    }

    setGuestCount(count);

    // Синхронизация массива имен с количеством
    const newNames = [...guestNames];
    if (count > guestNames.length) {
      // Добавляем пустые строки для новых гостей
      for (let i = guestNames.length; i < count; i += 1) {
        newNames.push('');
      }
    } else if (count < guestNames.length) {
      // Удаляем лишние имена
      newNames.splice(count);
    }
    setGuestNames(newNames);
  }, [guestNames]);

  const handleGuestNameChange = useCallback((index: number, name: string) => {
    setGuestNames((prev) => {
      const newNames = [...prev];
      newNames[index] = name;
      return newNames;
    });
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!response) {
      return;
    }

    const formData: RSVPFormData = {
      response,
      guestCount: response === 'yes' ? guestCount : 0,
      guestNames: response === 'yes' ? guestNames.filter((name) => name.trim()) : [],
      message: message.trim() || undefined,
    };

    // Валидация формы
    const validation = validateRSVPForm(formData);
    if (!validation.success) {
      return;
    }

    const result = await submitRSVP(inviteId, validation.data!);

    if (result.success && !result.error) {
      onSuccess();
    } else if (result.error) {
      showError(result.error);
    }
  }, [response, guestCount, guestNames, message, inviteId, submitRSVP, onSuccess, showError]);

  const isFormValid = useMemo(() => {
    if (!response) {
      return false;
    }
    if (response === 'yes' && guestCount < MIN_GUEST_COUNT) {
      return false;
    }
    return true;
  }, [response, guestCount]);

  // Создаем уникальные идентификаторы для каждого гостя
  // Используем стабильный id на основе позиции, чтобы избежать потери фокуса при вводе
  const guestsWithIds = useMemo(() => guestNames.map((name, position) => ({
    id: `${inviteId}-guest-${position}`,
    name,
    position,
  })), [guestNames, inviteId]);

  // Если успешно отправлено, не показываем форму
  if (success) {
    return null;
  }

  return (
    <motion.form
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={handleSubmit}
      className={styles.form}
    >
      <h2 className={styles.formTitle}>Подтвердите ваше присутствие</h2>

      {/* Кнопки ответа */}
      <div className={styles.responseButtons}>
        <motion.button
          type="button"
          onClick={() => handleResponseChange('yes')}
          className={`${styles.responseButton} ${response === 'yes' ? styles.active : ''}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <span className={styles.buttonIcon}>✓</span>
          <span>Приду</span>
        </motion.button>

        <motion.button
          type="button"
          onClick={() => handleResponseChange('no')}
          className={`${styles.responseButton} ${response === 'no' ? styles.active : ''}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <span className={styles.buttonIcon}>✗</span>
          <span>Не приду</span>
        </motion.button>
      </div>

      {/* Поля для гостей, если ответ "да" */}
      {response === 'yes' && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className={styles.guestSection}
        >
          <div className={styles.formGroup}>
            <label htmlFor="guestCount" className={styles.label}>
              Количество гостей
              <div className={styles.guestCountControls}>
                <motion.button
                  type="button"
                  onClick={() => handleGuestCountChange(guestCount - 1)}
                  disabled={guestCount <= MIN_GUEST_COUNT}
                  className={styles.countButton}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  −
                </motion.button>
                <span className={styles.guestCount}>{guestCount}</span>
                <motion.button
                  type="button"
                  onClick={() => handleGuestCountChange(guestCount + 1)}
                  disabled={guestCount >= MAX_GUEST_COUNT}
                  className={styles.countButton}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  +
                </motion.button>
              </div>
            </label>
          </div>

          {guestsWithIds.map((guest) => (
            <div key={guest.id} className={styles.formGroup}>
              <label htmlFor={`guestName-${guest.position}`} className={styles.label}>
                Имя гостя
                {' '}
                {guest.position + 1}
                {' '}
                {guest.position === 0 && '(опционально)'}
                <input
                  type="text"
                  id={`guestName-${guest.position}`}
                  value={guest.name}
                  onChange={(e) => handleGuestNameChange(guest.position, e.target.value)}
                  className={styles.input}
                  placeholder={`Имя гостя ${guest.position + 1}`}
                />
              </label>
            </div>
          ))}
        </motion.div>
      )}

      {/* Сообщение */}
      <div className={styles.formGroup}>
        <label htmlFor="message" className={styles.label}>
          Сообщение (опционально)
          <textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className={styles.textarea}
            placeholder="Оставьте сообщение для молодоженов..."
            rows={4}
          />
        </label>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={styles.error}
        >
          {error}
        </motion.div>
      )}

      <motion.button
        type="submit"
        disabled={loading || !isFormValid}
        whileHover={!loading && isFormValid ? { scale: 1.02 } : {}}
        whileTap={!loading && isFormValid ? { scale: 0.98 } : {}}
        className={styles.submitButton}
      >
        {loading ? 'Отправка...' : 'Отправить ответ'}
      </motion.button>
    </motion.form>
  );
});

RSVPForm.displayName = 'RSVPForm';

export default RSVPForm;
